import React, { useState, useEffect } from 'react';
import ManageDomainsModal from './ManageDomainsModal';
import { user_api } from '../../services/auth';
import { fetchUserDomainsFromAPI } from '../../services/domainService';

const UserForm = ({ initialData, especialistas, allUsers = [], onSubmit, onCancel, onManageDomains, mode, user}) => {
  const isEditingUser = Boolean(initialData && initialData.id && initialData.id > 0);

  const [formData, setFormData] = useState({});
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [activeTemplateUser, setActiveTemplateUser] = useState(null);
  const [newDomainInput, setNewDomainInput] = useState('');
  const [numCopiedDomains, setNumCopiedDomains] = useState(0);
  const [domainError, setDomainError] = useState('');
  const [numclienteError, setNumclienteError] = useState('');
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [nombreError, setNombreError] = useState('');
  const [correoError, setCorreoError] = useState('');
  const [newId, setNewId] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const loadFormData = async () => {
      const today = new Date().toISOString().split('T')[0];

      if (initialData) {
        setFormData({
          nombre: initialData.nombre || '',
          correo: initialData.correo || '',
          rol: initialData.rol || 'cliente',
          activo: initialData.activo === undefined || initialData.activo === '0' ? false : true,
          vence_en: initialData.vence_en || '',
          numcliente: initialData.numcliente || '',
          id_ejecutivo: initialData.id_ejecutivo,
          dominios: initialData.dominios ? [...initialData.dominios] : [],
        });
        if (initialData.id === 0 && initialData.dominios && initialData.dominios.length > 0) {
          setActiveTemplateUser(initialData);
        }
      } else {
        // Obtenemos el ID de forma asíncrona al abrir el formulario de creación
        let fetchedId = 0;
        try {
          const result = await user_api.get("/siguiente-id");
          if (result.data && result.data.success) {
            fetchedId = result.data.id;
          }
        } catch (err) {
          console.error("Error al obtener el siguiente ID:", err);
        }

        if (isMounted) {
          setNewId(fetchedId);
          setFormData({
            nombre: '',
            correo: '',
            rol: 'cliente',
            activo: true,
            vence_en: today,
            numcliente: `DM-${fetchedId}-${today}`,
            id_ejecutivo: undefined,
            dominios: [],
          });
        }
      }
    };

    loadFormData();

    return () => {
      isMounted = false; // Evita fugas de memoria si el componente se desmonta antes de resolver la promesa
    };
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const val = type === 'checkbox' ? (e.target).checked : value;

    if (name === 'nombre') {
      if (value.length > 100) {
        setNombreError('El nombre no puede superar los 100 caracteres.');
      } else {
        setNombreError('');
      }
    }

    if (name === 'correo') {
      if (value.length > 150) {
        setCorreoError('El correo electrónico no puede superar los 150 caracteres.');
      } else {
        setCorreoError('');
      }
    }

    if (name === 'numcliente') {
      if (value.length > 20) {
        setNumclienteError('El número de cliente no puede superar los 20 caracteres.');
      } else {
        setNumclienteError('');
      }
    }

    if (name === 'rol' && value !== 'cliente') {
      setActiveTemplateUser(null);
      setTemplateSearchQuery('');
    }

    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: name === 'id_ejecutivo' ? parseInt(value) || undefined : val,
      };

      if (name === 'rol' && value === 'especialista') {
        newData.vence_en = '';
        newData.id_ejecutivo = undefined;
        newData.dominios = [];
      }
      if (name === 'rol' && value === 'cliente' && !newData.vence_en) {
        newData.vence_en = new Date().toISOString().split('T')[0];
      }

      return newData;
    });
  };

  // Filter client users for template search
  const filteredTemplateUsers = allUsers.filter(u => {
    if (u.rol !== 'cliente') return false;
    if (!templateSearchQuery.trim()) return true;
    const q = templateSearchQuery.toLowerCase();
    const nameMatch = u.nombre.toLowerCase().includes(q);
    const numMatch = (u.numcliente || '').toLowerCase().includes(q);
    const emailMatch = u.correo.toLowerCase().includes(q);
    const domainMatch = (u.dominios || []).some(d => d.toLowerCase().includes(q));
    return nameMatch || numMatch || emailMatch || domainMatch;
  });

  const handleApplyTemplate = async (templateUser) => {
    if (formData.rol !== 'cliente') return;

    setActiveTemplateUser(templateUser);
    setTemplateSearchQuery('');

    let initialDominios = templateUser.dominios ? [...templateUser.dominios] : [];

    setNumclienteError('');

    setFormData(prev => ({
      ...prev,
      rol: templateUser.rol,
      vence_en: templateUser.vence_en || prev.vence_en,
      numcliente:`DM-${newId}-${new Date().toISOString().split('T')[0]}`,
      id_ejecutivo: templateUser.id_ejecutivo,
      dominios: initialDominios,
    }));

    // Fetch domains from backend API POST /users/dominios-usuario
    if (templateUser.id > 0) {
      const apiDomains = await fetchUserDomainsFromAPI(templateUser.id);
      if (apiDomains && apiDomains.length > 0) {
        const merged = Array.from(new Set([...initialDominios, ...apiDomains]));
        setNumCopiedDomains(apiDomains.length);
        setFormData(prev => ({
          ...prev,
          dominios: merged,
        }));
      }
    }
  };

  const handleClearTemplate = () => {
    setActiveTemplateUser(null);
    setTemplateSearchQuery('');
  };

  const handleAddDomain = (e) => {
    e.preventDefault();
    const cleanDomain = newDomainInput.trim().toLowerCase();
    
    if (!cleanDomain) return;

    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])+$/;
    if (!domainRegex.test(cleanDomain)) {
      setDomainError('Formato de dominio inválido (ej: ejemplo.com).');
      return;
    }

    if ((formData.dominios || []).includes(cleanDomain)) {
      setDomainError('Este dominio ya está en la lista.');
      return;
    }

    setDomainError('');
    setFormData(prev => ({
      ...prev,
      dominios: [...(prev.dominios || []), cleanDomain]
    }));
    setNewDomainInput('');
  };

  const handleRemoveDomain = (domainToRemove) => {
    setFormData(prev => ({
      ...prev,
      dominios: (prev.dominios || []).filter(d => d !== domainToRemove)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.nombre && formData.nombre.trim().length > 100) {
      setNombreError('El nombre no puede superar los 100 caracteres.');
      return;
    }

    if (formData.correo && formData.correo.trim().length > 150) {
      setCorreoError('El correo electrónico no puede superar los 150 caracteres.');
      return;
    }

    if (formData.numcliente && formData.numcliente.trim().length > 20) {
      setNumclienteError('El número de cliente no puede superar los 20 caracteres.');
      return;
    }

    const isCliente = formData.rol === 'cliente';
    const finalData = {
      ...formData,
      id: isEditingUser ? initialData?.id : undefined,
      dominios: isCliente ? formData.dominios : [],
      vence_en: isCliente ? formData.vence_en : new Date(), // /create-user es porfiado y no me deja marcar este campo como opcional.
      id_ejecutivo: isCliente ? formData.id_ejecutivo : undefined,
    };

    onSubmit(finalData);
  };

return (
    <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 transition-colors">
      <h3 className="text-xl font-bold text-gray-800 dark:text-slate-100 mb-6 flex items-center justify-between">
        <div className="flex items-center">
          <span className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded-lg flex items-center justify-center mr-3">
            <i className={`fas ${isEditingUser ? 'fa-user-edit' : 'fa-user-plus'}`}></i>
          </span>
          {isEditingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
        </div>
      </h3>

      {/* Template Search Box (Only shown when creating a new user) */}
      {!isEditingUser && allUsers.filter(u => u.rol === 'cliente').length > 0 && (
        <div className={`mb-6 p-4 rounded-2xl border transition-all ${
          formData.rol !== 'cliente'
            ? 'bg-gray-50/80 dark:bg-slate-800/40 border-gray-200 dark:border-slate-800 opacity-70'
            : 'bg-slate-50 dark:bg-slate-850 border-cyan-100 dark:border-slate-800'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2 text-cyan-800 dark:text-cyan-400 font-bold text-xs uppercase tracking-wider">
              <i className="fas fa-copy text-sm"></i>
              <span>Usar un cliente existente como plantilla</span>
              {formData.rol !== 'cliente' && (
                <span className="ml-1 text-[10px] font-semibold lowercase bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-800/40">
                  Deshabilitado (solo para rol Cliente)
                </span>
              )}
            </div>
            {activeTemplateUser && formData.rol === 'cliente' && (
              <button
                type="button"
                onClick={handleClearTemplate}
                className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 font-semibold flex items-center gap-1 transition"
              >
                <i className="fas fa-times"></i>
                <span>Quitar plantilla</span>
              </button>
            )}
          </div>

          <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
            {formData.rol !== 'cliente'
              ? 'La función de plantilla solo está disponible para nuevos usuarios con rol Cliente.'
              : 'Busca un cliente para copiar sus dominios y configuración a este nuevo registro.'}
          </p>

          {activeTemplateUser && formData.rol === 'cliente' ? (
            <div className="flex items-center justify-between p-3 bg-cyan-50 dark:bg-cyan-950/40 border border-cyan-200/80 dark:border-cyan-800/40 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-full bg-cyan-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                  {activeTemplateUser.nombre.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-xs font-bold text-cyan-950 dark:text-cyan-100">{activeTemplateUser.nombre}</p>
                  <p className="text-[11px] text-cyan-700 dark:text-cyan-300">
                    N° Cliente: {activeTemplateUser.numcliente || 'S/N'} • {activeTemplateUser.dominios?.length || 0} dominios copiados
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-extrabold uppercase px-2.5 py-1 bg-cyan-600 text-white rounded-md shadow-sm">
                Plantilla Activa
              </span>
            </div>
          ) : (
            <div className="relative">
              <div className="relative">
                <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                <input
                  type="text"
                  disabled={formData.rol !== 'cliente'}
                  placeholder={
                    formData.rol !== 'cliente'
                      ? 'Deshabilitado para este rol...'
                      : 'Buscar cliente por nombre, N° cliente, correo o dominio...'
                  }
                  value={templateSearchQuery}
                  onChange={(e) => setTemplateSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-8 py-2.5 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-cyan-500 outline-none bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100 text-xs font-medium disabled:bg-gray-100 dark:disabled:bg-slate-800/50 disabled:text-gray-400 dark:disabled:text-slate-500 disabled:cursor-not-allowed"
                />
                {templateSearchQuery && formData.rol === 'cliente' && (
                  <button
                    type="button"
                    onClick={() => setTemplateSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 text-xs"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>

              {/* Live Search Results List */}
              {formData.rol === 'cliente' && templateSearchQuery.trim().length > 0 && (
                <div className="mt-2 max-h-52 overflow-y-auto bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl divide-y divide-gray-100 dark:divide-slate-700/50 z-20">
                  {filteredTemplateUsers.length === 0 ? (
                    <div className="p-3.5 text-center text-xs text-gray-400 dark:text-slate-500 italic">
                      No se encontraron clientes con "{templateSearchQuery}"
                    </div>
                  ) : (
                    filteredTemplateUsers.map(templateUser => (
                      <button
                        key={templateUser.id}
                        type="button"
                        onClick={() => handleApplyTemplate(templateUser)}
                        className="w-full text-left p-3 hover:bg-cyan-50/70 dark:hover:bg-slate-700/50 transition flex items-center justify-between group"
                      >
                        <div className="min-w-0 pr-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-bold text-gray-800 dark:text-slate-100 group-hover:text-cyan-700 dark:group-hover:text-cyan-400 truncate">
                              {templateUser.nombre}
                            </span>
                            {templateUser.numcliente && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 rounded font-mono">
                                #{templateUser.numcliente}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-gray-400 dark:text-slate-400 truncate mt-0.5">
                            {templateUser.correo}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2 shrink-0">
                          <span className="text-[10px] font-bold text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-950/60 px-2 py-0.5 rounded-full border border-cyan-200/60 dark:border-cyan-800/40">
                            {templateUser.dominios?.length || 0} dominios
                          </span>
                          <i className="fas fa-chevron-right text-xs text-gray-300 group-hover:text-cyan-600 transition"></i>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Nombre Completo</label>
          <input
            required
            type="text"
            maxLength={100}
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-xl focus:ring-2 outline-none transition bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100 ${
              nombreError
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-200 dark:border-slate-700 focus:ring-cyan-500 focus:border-transparent'
            }`}
            placeholder="Juan Pérez"
          />
          <div className="flex justify-between items-center mt-1 text-xs">
            {nombreError ? (
              <span className="text-red-500 font-medium">{nombreError}</span>
            ) : (
              <span className="text-gray-400 text-[10px]">Máximo 100 caracteres</span>
            )}
            <span className={`text-[10px] font-mono ${
              (formData.nombre?.length || 0) > 100 ? 'text-red-500 font-bold' : 'text-gray-400'
            }`}>
              {formData.nombre?.length || 0}/100
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Correo Electrónico</label>
          <input
            required
            type="email"
            name="correo"
            maxLength={100}
            value={formData.correo}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-xl focus:ring-2 outline-none transition bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100 ${
              correoError
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-200 dark:border-slate-700 focus:ring-cyan-500 focus:border-transparent'
            }`}
            placeholder="juan@ejemplo.com"
          />
          <div className="flex justify-between items-center mt-1 text-xs">
            {correoError ? (
              <span className="text-red-500 font-medium">{correoError}</span>
            ) : (
              <span className="text-gray-400 text-[10px]">Máximo 150 caracteres</span>
            )}
            <span className={`text-[10px] font-mono ${
              (formData.correo?.length || 0) > 150 ? 'text-red-500 font-bold' : 'text-gray-400'
            }`}>
              {formData.correo?.length || 0}/150
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Rol del Sistema</label>
          <select
            name="rol"
            value={formData.rol}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100"
          >
            <option value="cliente">Cliente</option>
            <option value="especialista">Especialista</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Número de Cliente / Identificador</label>
          <input
            type="text"
            name="numcliente"
            value={formData.numcliente}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-xl focus:ring-2 outline-none transition bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100 ${
              numclienteError
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-200 dark:border-slate-700 focus:ring-cyan-500 focus:border-transparent'
            }`}
            placeholder="C-00123"
            
          />
          <div className="flex justify-between items-center mt-1 text-xs">
            {numclienteError ? (
              <span className="text-red-500 font-medium">{numclienteError}</span>
            ) : (
              <span className="text-gray-400 text-[10px]">Máximo 20 caracteres</span>
            )}
            <span className={`text-[10px] font-mono ${
              (formData.numcliente?.length || 0) > 20 ? 'text-red-500 font-bold' : 'text-gray-400'
            }`}>
              {formData.numcliente?.length || 0}/20
            </span>
          </div>
        </div>

        {formData.rol === 'cliente' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Fecha de Vencimiento</label>
              <input
                required={formData.rol === 'cliente'}
                type="date"
                name="vence_en"
                value={formData.vence_en}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100 [color-scheme:light] dark:[color-scheme:dark]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Ejecutivo Asignado (Especialista)</label>
              <select
                name="id_ejecutivo"
                value={formData.id_ejecutivo || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100"
                required
              >
                {especialistas.map(esp => (
                  <option key={esp.id} value={esp.id}>{esp.nombre}</option>
                ))}
              </select>
            </div>

            {/* Dominios asignados al usuario */}
            {isEditingUser ? (
              <div className="md:col-span-2 mt-2 bg-slate-50 dark:bg-slate-850 p-4 rounded-xl border border-gray-100 dark:border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                    <i className="fas fa-globe text-cyan-600 dark:text-cyan-400"></i>
                    <span>Dominios Web Asignados</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsManageModalOpen(true)}
                    className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-xs rounded-xl transition flex items-center gap-2 shadow-sm"
                  >
                    <i className="fas fa-globe"></i>
                    <span>Gestionar Dominios</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  Haz clic en "Gestionar Dominios" para desplegar la ventana de administración de dominios de este usuario.
                </p>
              </div>
            ) : (
              <div className="md:col-span-2 mt-2 bg-slate-50 dark:bg-slate-850 p-4 rounded-xl border border-gray-100 dark:border-slate-800 space-y-3">
                <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <i className="fas fa-globe text-cyan-600 dark:text-cyan-400"></i>
                    <span>Dominios Web Asignados ({(formData.dominios || []).length})</span>
                  </span>
                  <span className="text-[10px] text-gray-400 normal-case font-normal">Se asignarán al guardar el usuario</span>
                </label>

                {/* Input manual para agregar dominio en el mismo formulario */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Añadir dominio (ej: miempresa.com)"
                    value={newDomainInput}
                    onChange={(e) => {
                      setNewDomainInput(e.target.value);
                      setDomainError('');
                    }}
                    className="flex-1 px-3 py-1.5 border border-gray-200 dark:border-slate-700 rounded-lg text-xs outline-none bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100"
                  />
                  <button
                    type="button"
                    onClick={handleAddDomain}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-1.5 rounded-lg font-bold text-xs transition"
                  >
                    <i className="fas fa-plus mr-1"></i> Agregar
                  </button>
                </div>
                {domainError && <p className="text-xs text-red-500">{domainError}</p>}

                {/* Lista de chips de dominios */}
                {(formData.dominios || []).length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No hay dominios asignados actualmente.</p>
                ) : (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {(formData.dominios || []).map(dom => (
                      <span
                        key={dom}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cyan-50 dark:bg-cyan-950/40 text-cyan-800 dark:text-cyan-300 rounded-lg text-xs font-semibold border border-cyan-200/60 dark:border-cyan-800/40"
                      >
                        <i className="fas fa-globe text-[10px] text-cyan-600"></i>
                        <span>{dom}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveDomain(dom)}
                          className="text-gray-400 hover:text-red-500 ml-1 transition"
                          title="Quitar dominio"
                        >
                          <i className="fas fa-times text-[10px]"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div className="flex items-center space-x-3 mt-4">
          <input
            type="checkbox"
            name="activo"
            id="activo"
            checked={formData.activo}
            onChange={handleChange}
            className="w-5 h-5 text-cyan-600 dark:text-cyan-400 border-gray-300 dark:border-slate-700 rounded focus:ring-cyan-500 bg-transparent"
          />
          <label htmlFor="activo" className="text-sm font-medium text-gray-700 dark:text-slate-300 cursor-pointer select-none">
            Cuenta Activa
          </label>
        </div>
      </div>

      <div className="mt-8 flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition font-medium"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-6 py-2 rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 transition shadow-lg shadow-cyan-100 dark:shadow-none font-bold"
        >
          {isEditingUser ? 'Guardar Cambios' : 'Crear Usuario'}
        </button>
      </div>

      {isManageModalOpen && isEditingUser && initialData && (
        <ManageDomainsModal
          isOpen={isManageModalOpen}
          user={{ ...initialData, dominios: formData.dominios }}
          allUsers={allUsers}
          onClose={() => setIsManageModalOpen(false)}
          onSave={(userId, newDomains) => {
            setFormData(prev => ({ ...prev, dominios: newDomains }));
            setIsManageModalOpen(false);
          }}
        />
      )}
    </form>
  );
};

export default UserForm;
